import {computed, decorate, observable} from 'mobx';
import {PORT_INPUT, PORT_OUTPUT} from "../components/Midi";
import {
    ASSIGN1,
    ASSIGN2,
    ASSIGN3,
    DEFAULT_msb_mask,
    DEFAULT_sign_mask,
    MOD_ASSIGN_DEST,
    MOD_MATRIX_DESTINATION,
    MOD_MATRIX,
    multibytesValue,
    decodeModMatrixFW2,
    MOD_DESTINATION,
    MOD_GROUP_NAME,
    MOD_SRC_CYC_ENV,
    MOD_SRC_KEY_ARP,
    MOD_SRC_PRESS,
    MOD_SRC_LFO,
    MOD_SRC_ENV, FW2, CATEGORY,
    oscTypeName,
    decodeSampleIdx,
    decodeAssignSlot
} from "../model";
import {sampleNameFromIdx} from "../model/samples";
import {MSG_DATA, MSG_NAME, portById} from "../utils/midi";
import {h, hs} from "../utils/hexstring";
import {decompressFromEncodedURIComponent} from "lz-string";
import {getParameterByName} from "../utils/sharing";
import {
    extractVcodBytes,
    extractExtendedBytes,
    hashVcodBytes,
    getOverride,
    setOverride,
    clearOverrideFields,
    loadOverrides
} from "../utils/oscOverrides";

class State {

    // The number of the currently displayed preset
    preset_number = 0;  // 0..511 display as 1..512

    // input field in preset selector
    preset_number_string = '1';

    // The preset number used in MIDI
    preset_number_comm = null;      // 0..511 display as 1..512

    // All the presets
    // This is an array of {name: String; data: []}
    // We prefill the array with null value to avoid OutOfBound exceptions when accessing the array with MobX
    presets = new Array(512).fill(null);   // index 0..511

    // filename = null;    // presets file

    send_pc = true;    // if true send PC when changing preset

    lock = false;   // Used during preset reading to prevent concurrent reads.

    read_progress = 0;

    midi = {
        ports: {},
    };

    last_received_midi_msg = 0;

    error = 0;  // 0 means no error

    // User-supplied OSC-type overrides, keyed by VCOD-bytes hash. Loaded
    // from localStorage on startup. Bumped whenever an override is set or
    // cleared so MobX observers (Control.js) re-render with the new value.
    oscOverridesVersion = 0;

    // When false, all OSC-type override logic is skipped — no dropdown, no
    // per-preset hash/localStorage lookup. Big perf boost for preset-list
    // renders (each row would otherwise unpack+hash+lookup). Overrides in
    // storage are preserved; toggling back on re-applies them.
    taggingEnabled = true;

    constructor() {
        // Prime the overrides map from localStorage; reads go through
        // getOverride() which reads localStorage each time, but initial load
        // catches any parse errors early.
        loadOverrides();
        // console.log("constructor", this.preset_number_string, this.presets);
        const data = getParameterByName('data');
        // console.log("constructor", data);
        if (data) {
            const json = decompressFromEncodedURIComponent(data);
            if (json) {
                const preset = JSON.parse(json);
                if (preset) {
                    // console.log(json.length, json, preset);
                    this.presets[0] = preset;
                    this.preset_number = 0;
                    this.preset_number_string = '1';
                    // this.setPresetNumber(0);
                }
            }

        }
    }

    bytesToName(data) {
        // console.log("bytesToName", hs(data));
        let s = '';
        let i = 12;
        while (i < data.length && data[i] !== 0) {
            s += String.fromCharCode(data[i]);
            i++;
        }
        return s;
    }

    fwVersion() {
        // Every preset is routed through the FW2 model entries. Decoders are
        // marker-anchored on the unpacked stream (RE-32..RE-47), so they work
        // identically across all 7 fmt-byte variants seen in the wild
        // (0x0C/0x0D/0x0E/0x11/0x12/0x16/0x7F). The old FW1 dispatch on
        // data[0][12] === 0x0C routed legacy presets through a stale table
        // with mostly-null display mappings (Rise/Fall shape showed raw %
        // instead of "%", Cutoff showed % instead of Hz, etc.).
        return FW2;
    }

    currentOscTypeName() {
        return this.oscTypeNameFor(this.preset_number);
    }

    // OSC type name for a specific preset slot, with user override applied.
    // Used by ListView so the list mirrors what Control.js shows.
    oscTypeNameFor(index) {
        if (!this.presets.length || !this.presets[index]) return null;
        const data = this.presets[index].data;
        if (!data || data.length < 1) return null;
        const fw = FW2;
        if (!this.taggingEnabled) return oscTypeName(data, fw);
        // Read observable so MobX re-runs this computation when overrides change.
        // eslint-disable-next-line no-unused-expressions
        this.oscOverridesVersion;
        const hash = hashVcodBytes(extractVcodBytes(data));
        if (hash) {
            const entry = getOverride(hash);
            if (entry) return entry.oscType;
        }
        return oscTypeName(data, fw);
    }

    // Return the user-set OSC-type override for the current preset, or null
    // if none. User picks always win over the decoded value.
    currentOscTypeOverride() {
        if (!this.taggingEnabled) return null;
        if (!this.presets.length || !this.presets[this.preset_number]) return null;
        const data = this.presets[this.preset_number].data;
        if (!data || data.length < 1) return null;
        // eslint-disable-next-line no-unused-expressions
        this.oscOverridesVersion;
        const hash = hashVcodBytes(extractVcodBytes(data));
        if (!hash) return null;
        const entry = getOverride(hash);
        return entry ? entry.oscType : null;
    }

    setTaggingEnabled(enabled) {
        this.taggingEnabled = !!enabled;
    }

    setCurrentOscTypeOverride(oscType) {
        if (!this.presets.length || !this.presets[this.preset_number]) return;
        const preset = this.presets[this.preset_number];
        const data = preset.data;
        if (!data || data.length < 1) return;
        const bytes = extractVcodBytes(data);
        const hash = hashVcodBytes(bytes);
        if (!hash) return;
        setOverride(hash, { oscType, vcodBytes: bytes, presetName: preset.name || "" });
        this.oscOverridesVersion++;
    }

    clearCurrentOscTypeOverride() {
        if (!this.presets.length || !this.presets[this.preset_number]) return;
        const data = this.presets[this.preset_number].data;
        if (!data || data.length < 1) return;
        const hash = hashVcodBytes(extractVcodBytes(data));
        if (!hash) return;
        clearOverrideFields(hash, ["oscType"]);
        this.oscOverridesVersion++;
    }

    // Auto-decoded sample for the current preset — { idx, name } or null.
    // Independent of user override; the UI shows both ("mapped" vs "tagged").
    currentSampleDecoded() {
        if (!this.presets.length || !this.presets[this.preset_number]) return null;
        const data = this.presets[this.preset_number].data;
        if (!data || data.length < 1) return null;
        const idx = decodeSampleIdx(data);
        if (idx == null) return null;
        return { idx, name: sampleNameFromIdx(idx) };
    }

    // Return the user-set sample sub-type override for the current preset,
    // or null if none. Sample tagging is independent of OSC-type tagging:
    // the sample is only meaningful when the OSC type is Sample/Scan Grains/
    // Cloud Grains/Hit Grains, but we don't enforce that here — the UI hides
    // the dropdown when irrelevant.
    currentSampleOverride() {
        if (!this.taggingEnabled) return null;
        if (!this.presets.length || !this.presets[this.preset_number]) return null;
        const data = this.presets[this.preset_number].data;
        if (!data || data.length < 1) return null;
        // eslint-disable-next-line no-unused-expressions
        this.oscOverridesVersion;
        const hash = hashVcodBytes(extractVcodBytes(data));
        if (!hash) return null;
        const entry = getOverride(hash);
        if (!entry || entry.sampleIdx == null) return null;
        return { sample: entry.sample, sampleIdx: entry.sampleIdx };
    }

    setCurrentSampleOverride(sample, sampleIdx) {
        if (!this.presets.length || !this.presets[this.preset_number]) return;
        const preset = this.presets[this.preset_number];
        const data = preset.data;
        if (!data || data.length < 1) return;
        const bytes = extractVcodBytes(data);
        const hash = hashVcodBytes(bytes);
        if (!hash) return;
        const extendedBytes = extractExtendedBytes(data);
        setOverride(hash, {
            sample,
            sampleIdx,
            vcodBytes: bytes,
            extendedBytes,
            presetName: preset.name || "",
        });
        this.oscOverridesVersion++;
    }

    clearCurrentSampleOverride() {
        if (!this.presets.length || !this.presets[this.preset_number]) return;
        const data = this.presets[this.preset_number].data;
        if (!data || data.length < 1) return;
        const hash = hashVcodBytes(extractVcodBytes(data));
        if (!hash) return;
        clearOverrideFields(hash, ["sample", "sampleIdx", "extendedBytes"]);
        this.oscOverridesVersion++;
    }

    checkPreset(number) {
        // Every preset is now decoded via marker-anchored reads on the
        // unpacked stream, so the old packed-position 'EPanelc' signature
        // gate (which hid mod indicators on any preset matching it) is
        // obsolete. fw is always FW2 (see fwVersion()).
        if (this.presets && this.presets.length && this.presets[number]) {
            this.presets[number].fw = FW2;
            this.presets[number].supported = true;
        }
    }

    checkAllPresets() {
        // if (global.dev) console.log("checkAllPresets");
        for (let i=0; i<this.presets.length; i++) {
            if (this.presets[i] && !this.presets[i].hasOwnProperty("supported")) {
                this.checkPreset(i);
            }
        }
    }

    /**
     *
     * @param midiMessage midi message
     */
    importData(midiMessage) {

        // if (global.dev) console.log("midi message received", midiMessage, hs(midiMessage.data));

        const data = midiMessage.data;

        //TODO: extract preset num: NOT POSSIBLE, preset num is not in the answers

        if (data[0] === 0xF8) {
            // we ignore Timing Clock messages
            return;
        }

        if (data.length < 10) {
            if (global.dev) console.log("answer too short", hs(data));
            return;
        }

        if (!this.presets.length || (this.presets.length <= this.preset_number_comm) || this.presets[this.preset_number_comm] === null) {

            // create the preset struct:
            this.presets[this.preset_number_comm] = {
                name: null,
                supported: true,
                cat: 0,
                data:[]
            };

        }

        //
        // Store PRESET NAME and PRESET CAT:
        //
        if (data[8] === 0x52) {
            // console.log("answer 0x52 contains name");
            // console.log(hs(data));
            // console.log(hs(Array.from(data.slice(9, data.length - 1))));
            this.last_received_midi_msg = MSG_NAME;
            // state.data_name = Array.from(message_bytes.slice(9, message_bytes.length - 1));    // message_bytes is UInt8Array
            this.presets[this.preset_number_comm].name = this.bytesToName(Array.from(data.slice(9, data.length - 1)));    // message_bytes is UInt8Array

            console.log("save cat", this.presets[this.preset_number_comm].cat);
            this.presets[this.preset_number_comm].cat = data[19];
            return;
        }

        if (data[8] !== 0x16 && data[8] !== 0x17) {
            if (global.dev) console.warn(`ignore answer type 0x${h(data[8])}`, hs(data));
            return;
        }

        if (data.length !== 42) {
            if (global.dev) console.log("do not store answer", hs(data));
            return;
        }

        //
        // Store PRESET DATA:
        //
        this.last_received_midi_msg = MSG_DATA;
        this.presets[this.preset_number_comm].data.push(Array.from(data.slice(9, data.length - 1)));    // message_bytes is UInt8Array

        // console.log(this.presets[this.preset_number_comm].data.length - 1, ":", hs(data.slice(9, data.length - 1)));

    }

    /**
     * If string, range is 1..512
     * If number, range is 0..511
     * @param number
     */
setPresetNumber(number) {
    if (number === undefined || number === null) return;
    if ((typeof number !== 'string') && (typeof number !== 'number')) return;

    let num;
    let s = null;

    if (typeof number === 'string') {
        num = parseInt(number, 10);
    } else {
        num = number + 1;       // displayed value is 1..512
    }

    if (isNaN(num)) {
        num = 1;
    } else if (num > 512) {        // <- CHANGED
        s = '512';                 // <- CHANGED
        num = 512;                 // <- CHANGED
    } else if (num < 1) {
        s = '1';
        num = 1;
    } else {
        s = num.toString(10);
    }

    if (s === null) {
        this.preset_number_string = '';
    } else {
        this.preset_number_string = s;
        this.preset_number = num - 1;
    }
}

    addPort(port) {
        // eslint-disable-next-line
        if (this.midi.ports.hasOwnProperty(port.id) && this.midi.ports[port.id] !== null) {
            // already registered
            return false;
        }
        if (global.dev) console.log('State.addPort', port.type, port.name, port.id);
        this.midi.ports[port.id] = {
            type: port.type,
            name: port.name,
            manufacturer: port.manufacturer,
            enabled: false
        };
        return true;
    }

    removePort(port_id) {
        if (global.dev) console.log('State.removePort', port_id);
        this.midi.ports[port_id] = null;
    }

    removeAllPorts() {
        if (global.dev) console.log('State.removeAllPorts');
        this.midi.ports = {};
    }

    enablePort(port_id) {
        if (this.midi.ports[port_id]) {
            this.midi.ports[port_id].enabled = true;
        }
    }

    disablePort(port_id) {
        if (this.midi.ports[port_id]) {
            this.midi.ports[port_id].enabled = false;
        }
    }

    /**
     *
     * @param port
     * @param messageType only used if port is input
     * @param onMidiInputEvent only used if port is input
     */
    connectPort(port, messageType = null, onMidiInputEvent = null) {
        if (global.dev) console.log(`Midi.connectPort: ${port.type} ${port.id} ${port.name}`);
        if (port.type === PORT_INPUT) {
            if (port.hasListener(messageType, 'all', onMidiInputEvent)) {
                if (global.dev) console.warn(`Midi.connectPort: ${port.id} ${port.name} : ${messageType} messages on all channels listener already connected`);
            } else {
                if (global.dev) console.log(`Midi.connectPort: ${port.id} ${port.name} : add listener for ${messageType} messages on all channels`);
                port.addListener(messageType, 'all', onMidiInputEvent);
            }
        }

        // there is nothing else to do to "connect" an OUTPUT port.

        this.enablePort(port.id);
    }

    disconnectPort(port, updatePreferences=false) {
        if (port) {     // port is probably already null
            if (global.dev) console.log(`Midi.disconnectPort: ${port.type} ${port.id} ${port.name}`);
            if (port.type === PORT_INPUT) {
                if (port.removeListener) port.removeListener();
            }

            // there is nothing else to do to "connect" an OUTPUT port.

            this.disablePort(port.id);
        }
    }

    disconnectAllInputPorts(updatePreferences=false) {
        for (const port_id of Object.keys(this.midi.ports)) {
            if (this.midi.ports[port_id].type === PORT_INPUT) {
                this.disconnectPort(portById(port_id));
            }
        }
    }

    disconnectAllOutputPorts(updatePreferences=false) {
        for (const port_id of Object.keys(this.midi.ports)) {
            if (this.midi.ports[port_id].type === PORT_OUTPUT) {
                this.disconnectPort(portById(port_id));
            }
        }
    }

    disconnectAllPorts(updatePreferences=false) {
        if (global.dev) console.log('Midi.disconnectAllPorts');
        for (const port_id of Object.keys(this.midi.ports)) {
            this.disconnectPort(portById(port_id));
        }
    }

    /**
     * Returns true if at least one input is enabled
     */
    hasInputEnabled() {
        for (const port_id of Object.keys(this.midi.ports)) {
            if (this.midi.ports[port_id] && this.midi.ports[port_id].type === PORT_INPUT && this.midi.ports[port_id].enabled) return true;
        }
        return false;
    }

    /**
     * Returns true if at least one output is enabled
     */
    hasOutputEnabled() {
        for (const port_id of Object.keys(this.midi.ports)) {
            if (this.midi.ports[port_id] && this.midi.ports[port_id].type === PORT_OUTPUT && this.midi.ports[port_id].enabled) return true;
        }
        return false;
    }

    hasInputAndOutputEnabled() {
        return this.hasInputEnabled() && this.hasOutputEnabled();
    }

    presetExists(number) {
        return this.presets.length && (number < this.presets.length) && this.presets[number];
    }

    controlValue(m, return_raw=false, preset_number = -1) {

        const n = preset_number >= 0 ? preset_number : this.preset_number;

        if (!this.presets.length || (this.presets.length < n) || !this.presets[n]) {
            return 0;
        }

        if (!this.presets[n]) {
            return 0;
        }
        const data = this.presets[n].data;

        if (data.length < 39) return 0;  //FIXME

        let raw;
        if (typeof m.decoder === 'function') {
            const r = m.decoder(data);
            raw = r == null ? 0 : r;
        } else if (m.MSB) {
            const mask_msb = m.msb.length === 3 ? m.msb[2] : DEFAULT_msb_mask;
            raw = multibytesValue(
                data[m.MSB[0]][m.MSB[1]],
                data[m.LSB[0]][m.LSB[1]],
                data[m.msb[0]][m.msb[1]],
                mask_msb,
                0, 0);
        } else {
            raw = data[m.LSB[0]][m.LSB[1]];
        }

        return return_raw ? raw : (Math.round(raw * 1000 / 32768) / 10);
    }

    switchValue(m, return_raw=false) {

        if (!this.presets.length || (this.presets.length < this.preset_number) || !this.presets[this.preset_number]) {
            return 0;
        }

        const data = this.presets[this.preset_number].data;

        if (data.length < 39) return 0;  //FIXME

        let raw;
        if (typeof m.decoder === 'function') {
            const r = m.decoder(data);
            raw = r == null ? 0 : r;
        } else {
            const mask_msb = m.msb.length === 3 ? m.msb[2] : DEFAULT_msb_mask;
            raw = multibytesValue(
                data[ m.MSB[0] ][ m.MSB[1] ],
                data[ m.LSB[0] ][ m.LSB[1] ],
                data[ m.msb[0] ][ m.msb[1] ],
                mask_msb,
                0, 0);
        }

        if (return_raw) {
            return raw;
        } else {
            // Nearest-match: pick the entry whose `value` is closest to raw.
            // Decouples values[] order from matching logic so entries can be
            // listed in natural (UI/hardware) order rather than raw-ascending.
            let best = null;
            let bestDist = Infinity;
            for (let entry of m.values) {
                const d = Math.abs(raw - entry.value);
                if (d < bestDist) {
                    bestDist = d;
                    best = entry;
                }
            }
            return best ? best.name : raw;
        }
    }

    /**
     *
     * @param src key from MOD_SOURCE
     * @param dest key from MOD_DESTINATION
     * @param return_raw
     * @returns {number}
     */
    modMatrixValue(src, dest, return_raw=false) {

        if (!this.presets.length || (this.presets.length < this.preset_number) || !this.presets[this.preset_number]) {
            return 0;
        }

        const preset = this.presets[this.preset_number];
        const data = preset.data;

        if (data.length < 39) return 0;  //FIXME

        // RE-32: always use the unpacked-MIDI marker-anchored decoder.
        const raw = decodeModMatrixFW2(data, src, dest);
        if (raw === null) return 0;
        return return_raw ? raw : (Math.round(raw * 1000 / 32768) / 10);
    }

    /**
     *
     * @param slot
     */
    modAssignDest(slot) {
        if (!this.presets.length || (this.presets.length < this.preset_number) || !this.presets[this.preset_number]) {
            return 0;
        }
        const data = this.presets[this.preset_number].data;
        if (data.length < 39) return;  //FIXME
        const slotInfo = decodeAssignSlot(data, slot);
        if (!slotInfo) return null;
        return MOD_ASSIGN_DEST[slotInfo.modGroup];
    };

    /**
     *
     * @param slot
     */
    modAssignControlNum(slot) {
        if (!this.presets.length || (this.presets.length < this.preset_number)) {
            return 0;
        }
        const data = this.presets[this.preset_number].data;
        if (data.length < 39) return;  //FIXME
        const slotInfo = decodeAssignSlot(data, slot);
        if (!slotInfo) return 0;
        return slotInfo.control;
    };

    modDestName(dest) {
        if (!(dest === ASSIGN1 || dest === ASSIGN2 || dest === ASSIGN3)) {
            return MOD_MATRIX_DESTINATION[dest];
        }
        let group_name = '?';
        let control_name = '?';
        const dest_def = this.modAssignDest(dest);
        if (dest_def) {
            group_name = MOD_GROUP_NAME[dest_def.mod_group];
            const control = dest_def.control[this.modAssignControlNum(dest)];
            const dest_is_matrix =      // mod destination is the matrix itself
                control === MOD_SRC_CYC_ENV ||
                control === MOD_SRC_ENV ||
                control === MOD_SRC_LFO ||
                control === MOD_SRC_PRESS ||
                control === MOD_SRC_KEY_ARP;
            if (control) {
                control_name = MOD_DESTINATION[control];
                return dest_is_matrix ? `${control_name}-${group_name}` : `${group_name} ${control_name}`;
            }
        }
        return MOD_MATRIX_DESTINATION[dest];
    }

/*
    arpSyncOn() {

        if (!this.presets.length || (this.presets.length < this.preset_number) || !this.presets[this.preset_number]) {
            return 0;
        }
        const data = this.presets[this.preset_number].data;
        if (data.length < 39) return;  //FIXME

        console.log("arpSyncOn", this.switchValue(SWITCH[this.presets[this.preset_number].fw][ARP_SEQ_SYNC]));

        return this.switchValue(SWITCH[this.presets[this.preset_number].fw][ARP_SEQ_SYNC]) > 0;
    }
*/

presetName(number) {  //TODO: change method name
        if (this.presets.length && (number < this.presets.length) && this.presets[number]) {
            // console.log("preset name", this.presets[number]);
            const name = this.presets[number].name;
            // Show "Init" presets as blank since they are empty slots users can save to
            return (name === "Init") ? "" : name;
        } else {
            return null;
        }
    }

    presetCat(number) {  //TODO: change method name
        if (this.presets.length && (number < this.presets.length) && this.presets[number]) {
            // && this.presets[number].hasOwnProperty("cat")
            // console.log("preset cat", this.presets[number]);
            const name = this.presets[number].name;
            // Show "Init" presets category as blank since they are empty slots users can save to
            if (name === "Init") {
                return "";
            }
            if (this.presets[number].cat < CATEGORY.length)
                return CATEGORY[this.presets[number].cat];
            else
                return '';  //TODO: log error
        } else {
            return '';
        }
    }

    get presetNull() {
        if (this.presets && this.presets.length && this.presets[this.preset_number]) {
            return false;
        } else {
            return true;
        }
    }

/*
    oscType(number) {  //TODO: change method name
        if (this.presets.length && (number < this.presets.length) && this.presets[number]) {
            return this.presets[number].data[];
        } else {
            return '';
        }
    }
*/

}

// https://mobx.js.org/best/decorators.html
decorate(State, {
    midi: observable,
    // filename: observable,
    presets: observable,
    preset_number: observable,
    preset_number_string: observable,
    preset_number_comm: observable,
    send_pc: observable,
    lock: observable,
    read_progress: observable,
    error: observable,
    oscOverridesVersion: observable,
    taggingEnabled: observable,
    presetNull: computed
});

export const state = new State();
